import React, { useState, memo, useEffect } from 'react'
import { connect } from 'react-redux'
import { withRouter, Redirect } from 'react-router-dom'
import { compose } from 'redux'
import get from 'lodash/get'
import QRCode from 'react-qr-code'
import Button from 'britive-design-system/core/components/button'
import Textfield from 'britive-design-system/core/components/textfield'
import Typography from 'britive-design-system/core/components/typography'
import Textarea from 'britive-design-system/core/components/textarea'
import DialogPopup from 'britive-design-system/core/components/dialog'
import Radio from 'britive-design-system/core/components/radio'
import Checkbox from 'britive-design-system/core/components/checkbox'
import CopyButton from 'components/CopyButton'

import { getButtonLabel, getMfaHeadertitle } from '../utils'
import {
  MFA_SETUP,
  SOFTWARE_TOKEN_MFA,
  MFA_AUTH_CHOOSE_FACTOR,
  MFA_WEB_AUTHN_SETUP,
  MFA_REGISTER_CHOOSE_FACTOR,
  WEB_AUTHN_MFA,
  mfaFactor,
} from 'constants/authentication'

import Form from '../Form'
import './index.scss'

function MFAForm({
  challengeParameters,
  submitMFAOTP,
  submitMFAWebAuthn,
  errorMessage,
  fields,
  handleChange,
  history,
  showMFARegisterOption,
  setShowMFARegisterOption,
}) {
  const [showAuthenticatorCode, setShowAuthenticatorCode] = useState(false)
  const [preferredMFA, setPreferredMFA] = useState(false)
  const [mfaType, setMfaType] = useState('WebAuthn')
  const { challenge, QrCodeUrl, secret } = challengeParameters

  useEffect(() => {
    const savedMFA = localStorage.getItem('preferredMFA')
    if (savedMFA === 'OTP' || savedMFA === 'WebAuthn') {
      setMfaType(savedMFA)
      setPreferredMFA(true)
    }
    challenge === MFA_REGISTER_CHOOSE_FACTOR &&
      setShowMFARegisterOption(mfaFactor.webAuthN)
  }, [])

  async function goToLogin(event) {
    event.preventDefault()

    handleChange({ target: { name: 'password', value: '' } })

    history.push('/login')
  }

  if (
    ![
      SOFTWARE_TOKEN_MFA,
      MFA_SETUP,
      MFA_AUTH_CHOOSE_FACTOR,
      MFA_WEB_AUTHN_SETUP,
      MFA_REGISTER_CHOOSE_FACTOR,
      WEB_AUTHN_MFA,
    ].includes(challenge)
  ) {
    return <Redirect to="/login" />
  }

  const radioButtonProps = {
    name: 'mfaType',
    defaultValue: mfaType,
    options: [
      {
        label: 'WebAuthn',
        value: 'WebAuthn',
      },
      {
        label: 'OTP',
        value: 'OTP',
      },
    ],
    direction: 'vertical',
    onChange: event => {
      event.preventDefault()
      setMfaType(event.target.value)
      if (preferredMFA) {
        localStorage.setItem('preferredMFA', event.target.value)
      }
    },
  }
  return (
    <Form
      className="login-form"
      onSubmit={
        (mfaType === 'OTP' ||
          challenge === SOFTWARE_TOKEN_MFA ||
          challenge === MFA_SETUP) &&
        showMFARegisterOption !== mfaFactor.webAuthN
          ? submitMFAOTP
          : submitMFAWebAuthn
      }
      title={getMfaHeadertitle(mfaType, challenge, fields, showMFARegisterOption)}
      errorMessage={errorMessage}
    >
      {challenge === MFA_SETUP &&
        (showMFARegisterOption === mfaFactor.totp ||
          showMFARegisterOption === '') && (
          <>
            <div className="mfa">
              <Typography variant="body">
                Please download the Google Authenticator App from Google Play Store
                <i className="fab fa-google-play icon" />
                or Apple App Store
                <i className="fab fa-app-store-ios icon" />
                and register your device using the QR code shown.
              </Typography>
              <div className="qr-code">
                <QRCode value={QrCodeUrl} size={200} />
                <a
                  className="text-link"
                  onClick={() => setShowAuthenticatorCode(true)}
                >
                  Unable to scan?
                </a>
              </div>
            </div>
            <Typography variant="body">
              After completing device registration, please enter the OTP displayed on
              the Google Authenticator app and click on the {`"Submit"`} button
              below.
            </Typography>
          </>
        )}
      {(challenge === MFA_REGISTER_CHOOSE_FACTOR ||
        showMFARegisterOption === mfaFactor.webAuthN ||
        challenge === MFA_WEB_AUTHN_SETUP ||
        (challenge === WEB_AUTHN_MFA && fields.webAuthnName !== '')) && (
        <div className="webAuthn-field">
          <Typography variant="heading6">
            Register WebAuthn Authenticator 
          </Typography>

          <Textfield
            id="webAuthnName"
            name="webAuthnName"
            placeholder="Enter registration name"
            label="Device Name"
            value={fields.webAuthnName}
            onChange={handleChange}
          />
          <Textarea
            id="webAuthnDescription"
            name="webAuthnDescription"
            placeholder="Enter description"
            label="Description"
            value={fields.webAuthnDescription}
            onChange={handleChange}
          />
        </div>
      )}
      {showAuthenticatorCode && (
        <DialogPopup
          width={480}
          type="general"
          title="Register using key"
          message="Register using the following key"
          primaryButtonText="Done"
          onSubmit={() => setShowAuthenticatorCode(false)}
          onCancel={() => setShowAuthenticatorCode(false)}
        >
          <div className="mfa-secret">
            <Typography variant="label2">{secret}</Typography>
            <CopyButton textToCopy={secret} />
          </div>
        </DialogPopup>
      )}
      {(challenge === MFA_AUTH_CHOOSE_FACTOR ||
        (challenge === WEB_AUTHN_MFA && fields.webAuthnName === '')) && (
        <div>
          <Radio {...radioButtonProps} />
          {mfaType === 'OTP' && (
            <div className="otp-choose-field">
              <Textfield
                id="mfaOtp"
                name="mfaOtp"
                width={223}
                placeholder="OTP"
                value={fields.mfaOtp}
                onChange={handleChange}
              />
            </div>
          )}
          <div className="remember-mfa-factor">
            <Checkbox
              checked={preferredMFA}
              onChange={() => {
                setPreferredMFA(!preferredMFA)
                if (!preferredMFA) {
                  localStorage.setItem('preferredMFA', mfaType)
                } else {
                  localStorage.removeItem('preferredMFA')
                }
              }}
            />
            <Typography variant="label1">
              Remember my authentication option
            </Typography>
          </div>
        </div>
      )}
      {(challenge === SOFTWARE_TOKEN_MFA || challenge === MFA_SETUP) &&
        (showMFARegisterOption === mfaFactor.totp ||
          showMFARegisterOption === '') && (
          <div className="field">
            <Textfield
              id="mfaOtp"
              name="mfaOtp"
              label="OTP"
              placeholder="OTP"
              value={fields.mfaOtp}
              onChange={handleChange}
            />
          </div>
        )}
      <Button
        size="large"
        variant="primary"
        disabled={
          (challenge === MFA_WEB_AUTHN_SETUP ||
            challenge === MFA_REGISTER_CHOOSE_FACTOR ||
            showMFARegisterOption === mfaFactor.webAuthN) &&
          fields.webAuthnName === ''
        }
      >
        {getButtonLabel(mfaType, challenge, fields, showMFARegisterOption)}
      </Button>

      {showMFARegisterOption === mfaFactor.webAuthN && (
        <a
          className="text-link"
          onClick={e => {
            submitMFAOTP(e), setShowMFARegisterOption(mfaFactor.totp)
          }}
        >
          Setup OTP Instead
        </a>
      )}
      {showMFARegisterOption === mfaFactor.totp && (
        <a
          className="text-link"
          onClick={() => setShowMFARegisterOption(mfaFactor.webAuthN)}
        >
          Setup WebAuthn Instead
        </a>
      )}
      <a className="text-link" onClick={goToLogin}>
        Back to login
      </a>
    </Form>
  )
}

const mapStateToProps = state => ({
  challengeParameters: get(state, 'account.challengeParameters', {}),
  accessToken: get(state, 'account.accessToken'),
  refreshToken: get(state, 'account.refreshToken'),
  username: get(state, 'account.username'),
})

const withConnect = connect(mapStateToProps)

export default compose(withConnect, withRouter, memo)(MFAForm)
